import React, { useMemo } from 'react';

import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { navigationUiStateResource } from '@jira/router-resources__navigation-ui-state/src/navigationUiStateResource.tsx';
import { useResource, createRouterSelector } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createContainer } from '@atlassian/react-sweet-state';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useSuspenseResource } from '@atlassian/react-resource-router/src/resources/controllers/use-resource/suspense.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { RightSidebarState, LayoutState } from '../../../../common/types.tsx';
import { actions, type LayoutActions } from '../../actions/index.tsx';
import { layoutStore, DEFAULT_LEFT_SIDEBAR_WIDTH } from '../../store/index.tsx';
import { navPlatformRootStateResource } from './navPlatformRootStateResource.tsx';
import type { LayoutManagerQuery } from './__generated__/LayoutManagerQuery.graphql';
import type { LayoutManagerProps, LayoutContainerProps } from './types.tsx';

const LayoutContainer = createContainer<LayoutState, LayoutActions, LayoutContainerProps>(
	layoutStore,
	{
		displayName: 'JiraLayoutContainer',
		onInit:
			() =>
			({ dispatch }, { leftSidebar, rightSidebar, isChromeless }) => {
				dispatch(actions.setIsChromeless(isChromeless));
				dispatch(actions.setLeftSidebarState(leftSidebar));
				dispatch(actions.setRightSidebarState(rightSidebar));
			},
		onUpdate:
			() =>
			({ dispatch }, { isChromeless }) => {
				dispatch(actions.setIsChromeless(isChromeless));
			},
	},
);

const DEFFAULT_RIGHT_SIDEBAR_STATE: RightSidebarState = {
	stackState: [],
};

// Exported for unit test
// oxlint-disable-next-line typescript/no-invalid-void-type
export const useIsChromeless: (args_0: void) => boolean = createRouterSelector(
	(state) =>
		state.route.layout?.isChromeless === true ||
		state.route.entryPoint?.()?.toString() === 'ChromelessEntryPoint',
);

export const LayoutManager: React.FC<LayoutManagerProps> = componentWithCondition(
	() => fg('navx-global-side-nav'),
	LayoutManagerGSN,
	LayoutManagerNav4,
);

function LayoutManagerNewContent({
	children,
	isLeftSidebarCollapsed,
	leftSidebarWidth,
}: {
	children: React.ReactNode;
	isLeftSidebarCollapsed?: boolean | null;
	leftSidebarWidth?: number | null;
}) {
	const isChromeless = useIsChromeless();

	const leftSidebar = useMemo(
		() => ({
			isVisible: true,
			isCollapsed: isLeftSidebarCollapsed ?? false,
			width: leftSidebarWidth ?? DEFAULT_LEFT_SIDEBAR_WIDTH,
			isFlyoutOpen: false,
			hasUserPreference: false,
		}),
		[isLeftSidebarCollapsed, leftSidebarWidth],
	);

	return (
		<LayoutContainer
			isGlobal
			isChromeless={isChromeless}
			leftSidebar={leftSidebar}
			rightSidebar={DEFFAULT_RIGHT_SIDEBAR_STATE}
		>
			{children}
		</LayoutContainer>
	);
}

function LayoutManagerGSN({ children }: LayoutManagerProps) {
	const isAnonymous = useIsAnonymous();
	const isChromeless = useIsChromeless();

	// Both scenarios should use the default LayoutContainer
	if (isAnonymous || isChromeless) {
		return <LayoutManagerNewContent>{children}</LayoutManagerNewContent>;
	}

	return (
		<Placeholder name="nav4-ignore.gsn-layout-manager">
			<LayoutManagerNavPlat>{children}</LayoutManagerNavPlat>
		</Placeholder>
	);
}

function LayoutManagerNavPlat({ children }: LayoutManagerProps) {
	const { data } = useSuspenseResource(navPlatformRootStateResource);

	return data ? (
		<LayoutManagerNavPlatWithQuery queryReference={data}>{children}</LayoutManagerNavPlatWithQuery>
	) : (
		<LayoutManagerNewContent>{children}</LayoutManagerNewContent>
	);
}

function LayoutManagerNavPlatWithQuery({
	children,
	queryReference,
}: LayoutManagerProps & { queryReference: PreloadedQuery<LayoutManagerQuery> }) {
	const data = usePreloadedQuery(
		graphql`
			query LayoutManagerQuery(
				$navPlatSidebarDataEntityAri: ID!
				$ownerAri: ID
				$isAnonymous: Boolean!
			) {
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) @skip(if: $isAnonymous) {
					properties {
						nodes {
							key
							value
						}
					}
				}
			}
		`,
		queryReference,
	);

	let defaultCollapsed = false;
	let defaultWidth: number | undefined;

	if (data?.navPlatSidebarData?.properties?.nodes?.length) {
		data?.navPlatSidebarData.properties.nodes.forEach((item) => {
			if (item?.key === 'defaultCollapsed') {
				defaultCollapsed = item?.value === 'true';
			} else if (item?.key === 'defaultWidth') {
				defaultWidth = item?.value === '' ? undefined : Number(item?.value);
			}
		});
	}

	return (
		<LayoutManagerNewContent
			isLeftSidebarCollapsed={defaultCollapsed}
			leftSidebarWidth={defaultWidth}
		>
			{children}
		</LayoutManagerNewContent>
	);
}

function LayoutManagerNav4({ children }: LayoutManagerProps) {
	const { data } = useResource(navigationUiStateResource);
	const isChromeless = useIsChromeless();

	const isLeftSidebarCollapsed = data?.leftSidebar?.isCollapsed ?? false;
	const leftSidebarWidth = data?.leftSidebar?.width ?? DEFAULT_LEFT_SIDEBAR_WIDTH;
	const leftSidebar = useMemo(
		() => ({
			isVisible: true,
			isCollapsed: isLeftSidebarCollapsed,
			width: leftSidebarWidth,
			isFlyoutOpen: false,
			hasUserPreference: false,
		}),
		[isLeftSidebarCollapsed, leftSidebarWidth],
	);

	return (
		<LayoutContainer
			isGlobal
			isChromeless={isChromeless}
			leftSidebar={leftSidebar}
			rightSidebar={DEFFAULT_RIGHT_SIDEBAR_STATE}
		>
			{children}
		</LayoutContainer>
	);
}
